/**
 * 「產出預覽」分頁——發布前就看得到會產生什麼、還缺什麼。
 *
 * 一個景點滿載展開成 637 個檔，而編輯畫面是以欄位為單位的。填完之後「到底夠不夠」
 * 在別的地方看不出來，直到內容上了船、乘客站在玻璃前才發現某個語系少了三張圖。
 *
 * 兩個視角並列：**檔案樹**回答「會產生什麼」，**§15 回應**回答「船機會收到什麼」。
 * 但真正有用的是最上面的缺漏清單——那才回答「該補什麼」。
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { getSpotDisplayPreview, type SpotPreviewFile } from '../lib/api-client';

const SEVERITY_STYLE: Record<string, { bg: string; border: string; icon: string }> = {
  blocker: { bg: '#fef2f2', border: '#fca5a5', icon: '⛔' },
  warning: { bg: '#fffbeb', border: '#fcd34d', icon: '⚠' },
  info: { bg: '#f0f9ff', border: '#bae6fd', icon: 'ℹ' },
};

const SOURCE_LABEL: Record<string, string> = {
  generated: '產生',
  media: '媒體庫',
  qr: '發布時生成',
};

/** §15 `download_url` 的鍵，順序照規格書。 */
const DOWNLOAD_BUCKETS = ['video', 'pic', 'wav', 'txt', 'keyword', 'GPS'] as const;

/**
 * `op` 是整段 JSON 裡最容易看錯的一個字——四個英文單字對應四種完全不同的後果，
 * 其中 delete 會讓船上現有的內容消失。翻成一句話講清楚船機會做什麼。
 */
const OP_LABEL: Record<string, { text: string; color: string }> = {
  add: { text: 'add — 船上還沒有，會整包下載', color: '#047857' },
  modify: { text: 'modify — 船上已有，這次發布會覆蓋', color: '#1d4ed8' },
  delete: { text: 'delete — 船上那份會被刪掉，不下載任何檔', color: '#b91c1c' },
  none: { text: 'none — 不會出現在回應裡（沒內容，船上也沒有）', color: '#6b7280' },
};

/** `assets/12/en` → `en`；整個景點的 delete 沒有語系段。 */
function localeOf(a: { target_path?: string | null; delete_paths?: string[] }): string {
  const path = a.target_path ?? a.delete_paths?.[0] ?? '';
  const last = path.split('/')[2];
  return last ?? '整包';
}

export function SpotDisplayPreview({ spotId }: { spotId: string }): JSX.Element {
  const [expanded, setExpanded] = useState(false);

  const query = useQuery({
    queryKey: ['spot.display-preview', spotId],
    queryFn: () => getSpotDisplayPreview(spotId),
  });

  if (query.isLoading) return <div style={{ fontSize: 13 }}>計算中…</div>;
  if (query.error) return <div className="error-banner">{(query.error as Error).message}</div>;
  const data = query.data;
  if (!data) return <div />;

  const tree = groupByDirectory(data.files);
  const shownDirs = expanded ? tree : tree.slice(0, 6);
  const attractions = data.cms_update;

  return (
    <div style={{ margin: '12px 0' }}>
      {data.gaps.map((g) => {
        const s = SEVERITY_STYLE[g.severity] ?? SEVERITY_STYLE.info!;
        return (
          <div
            key={g.message}
            style={{
              background: s.bg,
              border: `1px solid ${s.border}`,
              borderRadius: 4,
              padding: 8,
              fontSize: 13,
              marginBottom: 6,
            }}
          >
            {s.icon} {g.message}
          </div>
        );
      })}

      {data.gaps.length === 0 && (
        <div
          style={{
            background: '#ecfdf5',
            border: '1px solid #6ee7b7',
            borderRadius: 4,
            padding: 8,
            fontSize: 13,
            marginBottom: 6,
          }}
        >
          ✓ 沒有發現缺漏。
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <fieldset
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            padding: 12,
            flex: '1 1 380px',
            minWidth: 0,
          }}
        >
          <legend style={{ fontSize: 13, fontWeight: 600 }}>
            船上檔案樹
            <span style={{ marginLeft: 8, fontWeight: 400, color: '#6b7280' }}>
              {data.counts.total} 個檔
            </span>
          </legend>

          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
            中 {data.counts.by_locale.zh}／英 {data.counts.by_locale.en}／日{' '}
            {data.counts.by_locale.jp}
            {'　'}· 一階 {data.counts.by_group.level1}／二階 {data.counts.by_group.level2}／推薦點{' '}
            {data.counts.by_group.poi}
          </div>

          {data.files.length === 0 ? (
            <div style={{ fontSize: 13, color: '#9ca3af' }}>目前不會產生任何檔案。</div>
          ) : (
            <div style={{ fontFamily: 'Consolas, Menlo, monospace', fontSize: 12 }}>
              {shownDirs.map(([dir, files]) => (
                <div key={dir} style={{ marginBottom: 6 }}>
                  <div style={{ color: '#2563eb' }}>
                    {dir}/ <span style={{ color: '#9ca3af' }}>({files.length})</span>
                  </div>
                  {files.map((f) => (
                    <div
                      key={f.path}
                      style={{ display: 'flex', gap: 8, paddingLeft: 12, color: '#374151' }}
                    >
                      <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-all' }}>
                        {f.path.split('/').pop()}
                      </span>
                      <span style={{ color: '#9ca3af', flexShrink: 0 }}>{f.field}</span>
                      <span
                        style={{ color: '#d1d5db', flexShrink: 0, width: 64, textAlign: 'right' }}
                      >
                        {f.size === null ? SOURCE_LABEL[f.source] : formatSize(f.size)}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
              {tree.length > shownDirs.length && (
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  style={{
                    marginTop: 4,
                    fontSize: 12,
                    color: '#2563eb',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  還有 {tree.length - shownDirs.length} 個資料夾⋯展開全部
                </button>
              )}
            </div>
          )}
        </fieldset>

        <fieldset
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            padding: 12,
            flex: '1 1 320px',
            minWidth: 0,
          }}
        >
          <legend style={{ fontSize: 13, fontWeight: 600 }}>船機會收到的內容</legend>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 8px' }}>
            靠港時 <code>/api/cms/update</code> 回應的 <code>attractions[]</code> 裡，這個景點的那
            <strong>幾</strong>筆——<strong>一個語系一筆</strong>。<code>target_path</code>{' '}
            指到哪個語系，<code>download_url</code> 就只放那個語系的檔（<code>roiN.txt</code>{' '}
            不分語系，每筆都給）。值是船機會拿到的完整位址；<code>{'{version}'}</code>{' '}
            與簽章參數要等發布才填得了，主機、bucket、資產路徑三段不變。
          </p>
          {attractions.map((a) => {
            const op = OP_LABEL[a.op] ?? OP_LABEL.none!;
            return (
              <div key={localeOf(a)} style={{ fontSize: 12, marginBottom: 4 }}>
                <span style={{ fontWeight: 600, color: op.color }}>
                  {localeOf(a)}
                  {'　'}
                  {op.text}
                </span>
                {a.op !== 'delete' && (
                  <div style={{ color: '#6b7280', paddingLeft: 12 }}>
                    {DOWNLOAD_BUCKETS.map((k) => `${k} ${a.download_url[k].length}`).join('　· ')}
                  </div>
                )}
              </div>
            );
          })}
          <div style={{ height: 6 }} />
          <pre
            style={{
              margin: 0,
              padding: 8,
              background: '#f9fafb',
              border: '1px solid #f3f4f6',
              borderRadius: 4,
              fontSize: 12,
              overflowX: 'auto',
              // 一個滿載的景點有 600 多個路徑；不限高會把整頁撐到捲不完
              maxHeight: 420,
              overflowY: 'auto',
              whiteSpace: 'pre',
            }}
          >
            {JSON.stringify(attractions, null, 2)}
          </pre>
        </fieldset>
      </div>
    </div>
  );
}

/** 依目錄分組，目錄內依檔名排序——與船上的資料夾結構一致。 */
function groupByDirectory(files: readonly SpotPreviewFile[]): Array<[string, SpotPreviewFile[]]> {
  const map = new Map<string, SpotPreviewFile[]>();
  for (const f of files) {
    const dir = f.path.split('/').slice(0, -1).join('/');
    const list = map.get(dir);
    if (list) list.push(f);
    else map.set(dir, [f]);
  }
  return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
